package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.automation.showPretranslateStarted
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.actionKey
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/** One request per pause in typing, not one per keystroke — the editor's own search box waits the
 *  same length before it moves. */
private const val SEARCH_DEBOUNCE_MS = 220L

@Page("/app/projects/{slug}/keys")
@Layout(".components.layouts.AppLayout")
@Composable
fun ProjectKeysPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"].orEmpty()
    val scope = rememberCoroutineScope()
    val toasts = rememberToasts()
    val verifiedRoute = liveProjectRoute(slug)
    val authority = ProjectAuthority.resolve(verifiedRoute?.project, Session.organizations)
    val structureClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.STRUCTURE,
    )
    val readClaim = ProjectActionClaim.capture(
        verifiedRoute,
        Session.organizations,
        ProjectPrivilege.READ,
    )
    val authorityKey = verifiedRoute?.actionKey()
    val authorityStateKey = verifiedRoute?.authoritySubject(Session.organizations)
    val isReadCurrent: () -> Boolean = {
        readClaim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true
    }

    // Keyed on nothing but its own state: using global search while already on this page changes
    // only the query parameter, and a value seeded once from it would silently stop tracking further
    // navigations to the same page.
    var search by remember { mutableStateOf(ctx.route.queryParams["search"].orEmpty()) }
    var debouncedSearch by remember { mutableStateOf(search.trim()) }
    // Seeded from the URL: the overview's file rows and its "Browse all keys" button both land
    // here, and a `?fileId=` that the page ignores is a link that silently lies about where it went.
    var fileId by remember { mutableStateOf(ctx.route.queryParams["fileId"].orEmpty()) }
    var locale by remember { mutableStateOf("") }
    var stateFilter by remember { mutableStateOf(KeyStateFilter.ANY) }
    var tag by remember { mutableStateOf("") }
    var refreshKey by remember { mutableStateOf(0) }
    var loadingMore by remember(authorityStateKey) { mutableStateOf(false) }

    LaunchedEffect(search) {
        delay(SEARCH_DEBOUNCE_MS)
        debouncedSearch = search.trim()
    }

    var foundation by remember(authorityStateKey) { mutableStateOf<KeysFoundationData?>(null) }
    var foundationError by remember(authorityStateKey) { mutableStateOf<String?>(null) }

    LaunchedEffect(slug, authorityKey, authority.role, refreshKey) {
        val key = authorityKey
        if (key == null || readClaim == null || !isReadCurrent()) return@LaunchedEffect
        foundation = null
        foundationError = null
        try {
            val loaded = loadKeysFoundation(
                key = key,
                expectedSlug = slug,
                memberships = Session.organizations,
            )
            if (!isReadCurrent()) return@LaunchedEffect
            foundation = loaded
            // The "state" filter and the row's link into the editor both need one concrete locale to
            // mean anything; default to the project's first target locale rather than leaving both
            // unusable until somebody notices there is a picker.
            if (locale.isBlank()) locale = loaded.targetLocales.firstOrNull().orEmpty()
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (isReadCurrent()) {
                foundationError = failure.safeKeysMessage(
                    tr("app.projects.slug.project_keys_page.couldnt_load_this_project_retry", "Couldn't load this project. Retry?"),
                )
            }
        }
    }

    val query = KeysQuery(debouncedSearch, fileId, locale, stateFilter.apiState, tag.trim())
    var sliceState by remember(authorityStateKey) { mutableStateOf<Async<KeysSlice>>(Async.Loading) }
    // A filter change is a refresh of results already on screen, not a fresh page load. Dropping
    // back to Async.Loading unmounted KeysContent — and with it the search field being typed in.
    var refreshing by remember(authorityStateKey) { mutableStateOf(false) }
    var refreshError by remember(authorityStateKey) { mutableStateOf<String?>(null) }

    LaunchedEffect(foundation, query, authorityKey, authority.role) {
        val current = foundation ?: return@LaunchedEffect
        val key = authorityKey ?: return@LaunchedEffect
        if (!isReadCurrent()) return@LaunchedEffect
        val hadRows = sliceState is Async.Success
        if (hadRows) {
            refreshing = true
            refreshError = null
        } else {
            sliceState = Async.Loading
        }
        try {
            val loaded = loadKeysSlice(
                key = key,
                expectedSlug = slug,
                targetLocales = current.targetLocales,
                query = query,
                after = null,
                memberships = Session.organizations,
            )
            if (isReadCurrent()) {
                sliceState = Async.Success(loaded)
                refreshError = null
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (isReadCurrent()) {
                val message = failure.safeKeysMessage(
                    tr("app.projects.slug.project_keys_page.couldnt_load_these_keys_retry", "Couldn't load these keys. Retry?"),
                )
                // Rows already on screen are still true and still usable. Replacing them with a
                // full-page error would throw away the reader's place to report a failed refresh.
                if (hadRows) refreshError = message else sliceState = Async.Failure(message)
            }
        } finally {
            refreshing = false
        }
    }

    fun loadMore() {
        val current = foundation ?: return
        val slice = (sliceState as? Async.Success)?.value ?: return
        val after = slice.nextCursor ?: return
        val requestedQuery = query
        val key = authorityKey ?: return
        if (!isReadCurrent()) return
        scope.launch {
            if (!isReadCurrent()) return@launch
            loadingMore = true
            try {
                val next = loadKeysSlice(
                    key = key,
                    expectedSlug = slug,
                    targetLocales = current.targetLocales,
                    query = requestedQuery,
                    after = after,
                    memberships = Session.organizations,
                )
                // The filters may have changed while this was in flight; a page that no longer
                // matches what is on screen must not be spliced onto it.
                if (requestedQuery == query && isReadCurrent()) {
                    sliceState = Async.Success(
                        slice.copy(
                            rows = slice.rows + next.rows,
                            coverage = slice.coverage + next.coverage,
                            nextCursor = next.nextCursor,
                        ),
                    )
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (isReadCurrent()) {
                    toasts.show(
                        failure.loadMoreKeysMessage(),
                        LToastVariant.DANGER,
                        TOAST_UNTIL_DISMISSED,
                    )
                }
            } finally {
                if (isReadCurrent()) loadingMore = false
            }
        }
    }

    Column(Modifier.fillMaxWidth().gap(Space.XXL)) {
        val currentFoundation = foundation
        when {
            foundationError != null -> {
                PageHeader(t("app.projects.slug.project_keys_page.keys", "Keys"), t("app.projects.slug.project_keys_page.manage_source_strings_metadata_and_translation", "Manage source strings, metadata, and translation coverage."))
                ErrorState(foundationError.orEmpty(), onRetry = { refreshKey++ })
            }
            currentFoundation == null -> {
                PageHeader(t("app.projects.slug.project_keys_page.keys", "Keys"), t("app.projects.slug.project_keys_page.manage_source_strings_metadata_and_translation", "Manage source strings, metadata, and translation coverage."))
                LoadingState(rows = 7)
            }
            else -> when (val slice = sliceState) {
                Async.Loading -> {
                    PageHeader(t("app.projects.slug.project_keys_page.keys", "Keys"), t("app.projects.slug.project_keys_page.manage_source_strings_metadata_and_translation", "Manage source strings, metadata, and translation coverage."))
                    LoadingState(rows = 7)
                }
                is Async.Failure -> {
                    PageHeader(t("app.projects.slug.project_keys_page.keys", "Keys"), t("app.projects.slug.project_keys_page.manage_source_strings_metadata_and_translation", "Manage source strings, metadata, and translation coverage."))
                    AsyncFailureState(
                        slice,
                        subject = t("app.projects.slug.project_keys_page.these_keys", "these keys"),
                        onRetry = { refreshKey++ },
                    )
                }
                is Async.Success -> KeysContent(
                    onOpenDeleted = { ctx.router.navigateTo("/app/projects/$slug/keys/deleted") },
                    foundation = currentFoundation,
                    result = slice.value,
                    authority = authority,
                    authoritySubject = requireNotNull(authorityStateKey),
                    isMutationCurrent = {
                        structureClaim?.remainsAuthorized(
                            liveProjectRoute(slug),
                            Session.organizations,
                        ) == true
                    },
                    mutationClaim = structureClaim,
                    currentRoute = { liveProjectRoute(slug) },
                    currentMemberships = { Session.organizations },
                    slug = slug,
                    search = search,
                    onSearchChange = { search = it },
                    fileId = fileId,
                    onFileIdChange = { fileId = it },
                    locale = locale,
                    onLocaleChange = { locale = it },
                    stateFilter = stateFilter,
                    onStateFilterChange = { stateFilter = it },
                    tag = tag,
                    onTagChange = { tag = it },
                    loadingMore = loadingMore,
                    refreshing = refreshing,
                    refreshError = refreshError,
                    onLoadMore = ::loadMore,
                    onOpenFiles = { ctx.router.tryRoutingTo("/app/projects/$slug/settings?tab=files") },
                    onOpenPermalink = { row ->
                        ctx.router.tryRoutingTo("/app/projects/$slug/strings/${row.key.id}")
                    },
                    // Absent for any role the server would refuse to spend for. Named as the
                    // spend rather than borrowed from the structure claim, which happens to apply
                    // the same owner-or-admin bar for an entirely different reason.
                    //
                    // The run is priced and started on the page beside the filter that scoped it;
                    // what arrives here is the id of a run that is now going — fresh, or the one a
                    // `409 job_already_running` named. This end owns the router, so it opens the
                    // receipt.
                    onPretranslateStarted = if (authority.canSpendCredits) {
                        { jobId, strings ->
                            showPretranslateStarted(
                                toasts = toasts,
                                router = ctx.router,
                                projectSlug = slug,
                                jobId = jobId,
                                strings = strings,
                            )
                        }
                    } else {
                        null
                    },
                    onChanged = { refreshKey++ },
                    notify = toasts::show,
                )
            }
        }
    }
    ToastHost(toasts)
}

/**
 * What a failed "load more" says, in the reader's language.
 *
 * The last raw literal in a file that had translated everything around it: the two sibling
 * fallbacks above it — the project read and the keys read — go through the catalogue, and this one,
 * which is the one a long list on a bad connection actually produces, did not. It raises an
 * until-dismissed toast, so a reader whose interface is German was left with an English sentence
 * on screen until they closed it by hand.
 *
 * A function rather than an inline `tr` so a test can read the sentence without a page, a session
 * and a network. The server's own message still wins when it sent one; what is asserted is the
 * fallback, which is what a dropped connection leaves behind.
 */
internal fun Throwable.loadMoreKeysMessage(): String = safeKeysMessage(
    tr(
        "app.projects.slug.project_keys_page.couldnt_load_more_keys_retry",
        "Couldn't load more keys. Retry?",
    ),
)
